import { recordPlace, titleFor, type NavAction, type Place } from './places';
import { navKey, onNavUserChange } from './session';

/**
 * The tab's app places (P13), shared by both "Wstecz / Dalej" bars (desktop and phone). Kept in the tab's session
 * storage, so a reload still knows what is behind and ahead; storage may be blocked (private mode) — then only memory.
 */
const key = () => navKey('nav.places');

export type NavState = { stack: Place[]; idx: number };

function load(): Place[] {
  try {
    const raw = sessionStorage.getItem(key());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((p): p is Place => !!p && typeof p.idx === 'number' && typeof p.path === 'string' && typeof p.title === 'string') : [];
  } catch {
    return [];
  }
}

function save(stack: Place[]) {
  try {
    sessionStorage.setItem(key(), JSON.stringify(stack));
  } catch {
    // storage full or blocked: the buttons still work for this page's life
  }
}

let state: NavState = { stack: load(), idx: 0 };
/** The last name a screen gave each address (it may name itself before or after the place is recorded). */
const titles = new Map<string, string>();
const listeners = new Set<() => void>();

function set(next: NavState) {
  state = next;
  save(next.stack);
  for (const l of listeners) l();
}

export const navStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  snapshot: (): NavState => state,
  record(at: { idx: number; path: string; action: NavAction }) {
    const title = titles.get(at.path) ?? titleFor(at.path.split('?')[0]!);
    set({ stack: recordPlace(state.stack, { ...at, title }), idx: at.idx });
  },
  setTitle(path: string, title: string) {
    titles.set(path, title);
    const here = state.stack.find((p) => p.idx === state.idx);
    if (here && here.path === path && here.title !== title) set({ ...state, stack: state.stack.map((p) => (p === here ? { ...p, title } : p)) });
  },
};

/** Another account (or a logout): its own places only, and no names remembered from before. */
export function resetNavStore() {
  titles.clear();
  state = { stack: load(), idx: 0 };
  for (const l of listeners) l();
}
onNavUserChange(resetNavStore);
